import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import cookie from "react-cookies";
import { getCreatorBalance, getCreatorPayouts } from "../../../services/creatorApi";
import { TrainingLoading } from "../../academy/AcademyShared";
import CreatorBankingCard from "./CreatorBankingCard";
import { CreatorLayout, CREATOR_CRUMBS, CreatorStatusChip } from "./CreatorShared";

export default function CreatorPayments() {
  const [balance, setBalance] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!cookie.load("candidateKey")) {
      window.location.href = "/login?next=/me/creator/payments";
      return;
    }
    setLoading(true);
    Promise.all([getCreatorBalance(), getCreatorPayouts()])
      .then(([b, p]) => {
        setBalance({
          ...(b.balance || {}),
          approved_work: b.approved_work || [],
        });
        setPayouts(p.payouts || []);
      })
      .catch((e) => setError(e.message || "Could not load payments"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <CreatorLayout breadcrumbs={CREATOR_CRUMBS.payments} backTo="/me/creator">
        <TrainingLoading label="Loading payments..." />
      </CreatorLayout>
    );
  }

  return (
    <CreatorLayout breadcrumbs={CREATOR_CRUMBS.payments} backTo="/me/creator">
      <section className="creator-hero">
        <p className="training-kicker">Payments</p>
        <h2 className="creator-hero__title">Your balance</h2>
        <p className="creator-hero__lede">
          Submitting clips creates a pending claim. Approval makes it payable. QikTruck pays by EFT after banking details are confirmed.
        </p>
      </section>

      {error ? <p className="creator-error">{error}</p> : null}

      {balance ? (
        <>
          <div className="training-stats creator-stats">
            <div className="training-stat">
              <div className="training-stat-value">{balance.pending_label}</div>
              <div className="training-stat-label">Pending</div>
            </div>
            <div className="training-stat">
              <div className="training-stat-value">{balance.payable_label}</div>
              <div className="training-stat-label">Payable</div>
            </div>
            <div className="training-stat">
              <div className="training-stat-value">{balance.paid_label}</div>
              <div className="training-stat-label">Paid</div>
            </div>
          </div>

          <div className="training-continue-card creator-continue">
            <span className="training-continue-label">Available balance</span>
            <strong className="training-continue-title">{balance.available_label}</strong>
            <span className="training-continue-meta">{balance.friday_note}</span>
          </div>

          <CreatorBankingCard />

          <h3 className="creator-section-title">Approved work</h3>
          {!balance.approved_work?.length ? (
            <p className="creator-muted">No approved scripts yet.</p>
          ) : (
            <div className="creator-work-list">
              {balance.approved_work.map((row) => (
                <article key={row.submission_id} className="training-card creator-work-card">
                  <div className="creator-work-card__top">
                    <CreatorStatusChip status={row.ledger_status || "payable"} />
                    <span className="creator-script-card__pay">{row.amount_label}</span>
                  </div>
                  <h3 className="creator-work-card__title">
                    {row.code} · {row.title}
                  </h3>
                  <Link className="training-btn-secondary" to={`/me/creator/work/${row.submission_id}`}>
                    Open
                  </Link>
                </article>
              ))}
            </div>
          )}

          <h3 className="creator-section-title">Payout history</h3>
          {!payouts.length ? (
            <p className="creator-muted">No payouts yet.</p>
          ) : (
            <div className="creator-work-list">
              {payouts.map((row) => (
                <article key={row.id} className="training-card creator-work-card">
                  <div className="creator-work-card__top">
                    <CreatorStatusChip status={row.status === "paid" ? "paid" : "payable"} />
                    <span className="creator-script-card__pay">{row.amount_label}</span>
                  </div>
                  <h3 className="creator-work-card__title">
                    {row.script_code} · {row.script_title}
                  </h3>
                  <p className="creator-work-card__meta">
                    {row.period_label || "Ledger"}
                    {row.paid_at ? ` · Paid ${new Date(row.paid_at).toLocaleDateString()}` : ""}
                  </p>
                </article>
              ))}
            </div>
          )}
        </>
      ) : null}
    </CreatorLayout>
  );
}
